import { NavLink, Outlet, useLocation } from 'react-router-dom'
import { BarChart3, Bike, Boxes, ChefHat, FileText, Gift, GlassWater, LayoutDashboard, LogOut, Menu, PackageOpen, PanelLeftClose, ReceiptText, Settings, SlidersHorizontal, UtensilsCrossed, Volume2, VolumeX, WalletCards, UsersRound, UserRoundCheck, Ban, Ticket, HandCoins, CalendarClock, ShieldCheck } from 'lucide-react'
import { useEffect, useMemo, useState } from 'react'
import { useAuth } from '../context/AuthContext'
import { supabase } from '../lib/supabase'
import { getSoundEnabled, playNotificationSound, setSoundEnabled } from '../lib/notificationSound'

const links = [
  { to: '/garcom', label: 'Área do garçom', icon: UserRoundCheck, roles: ['waiter'] },
  { to: '/painel', label: 'Visão geral', icon: LayoutDashboard, roles: ['super_admin','admin','manager','cashier','kitchen','bar','stock','finance'] },
  { to: '/mesas', label: 'Mesas', icon: UtensilsCrossed, roles: ['super_admin','admin','manager','waiter'] },
  { to: '/produtos', label: 'Produtos', icon: PackageOpen, roles: ['super_admin','admin','manager'] },
  { to: '/estoque', label: 'Estoque', icon: Boxes, roles: ['super_admin','admin','manager','stock'] },
  { to: '/fiscal', label: 'Fiscal / NFC-e', icon: FileText, roles: ['super_admin','admin','manager','finance'] },
  { to: '/clientes', label: 'Clientes', icon: UsersRound, roles: ['super_admin','admin','manager','cashier','waiter'] },
  { to: '/fidelidade', label: 'Fidelidade', icon: Gift, roles: ['super_admin','admin','manager','cashier','waiter'] },
  { to: '/comandas', label: 'Central de Comandas', icon: Ticket, roles: ['super_admin','admin','manager','cashier','waiter'] },
  { to: '/pedidos-fechados', label: 'Pedidos fechados', icon: ReceiptText, roles: ['super_admin','admin','manager','cashier'] },
  { to: '/pedidos', label: 'Pedidos', icon: ReceiptText, roles: ['super_admin','admin','manager','cashier','waiter'] },
  { to: '/cancelamentos', label: 'Cancelamentos', icon: Ban, roles: ['super_admin','admin','manager'] },
  { to: '/auditoria-mesas', label: 'Auditoria de mesas', icon: ShieldCheck, roles: ['super_admin','admin','manager'] },
  { to: '/vendas-rapidas', label: 'Venda sem mesa / Balcão', icon: Bike, roles: ['super_admin','admin','manager','cashier','waiter'] },
  { to: '/caixa', label: 'Caixa e pagamentos', icon: WalletCards, roles: ['super_admin','admin','manager','cashier','finance'] },
  { to: '/contas-a-pagar', label: 'Contas a pagar', icon: CalendarClock, roles: ['super_admin','admin','manager','cashier','finance'] },
  { to: '/fiado', label: 'Fiado dos clientes', icon: HandCoins, roles: ['super_admin','admin','manager','cashier','finance'] },
  { to: '/caixa-operacoes', label: 'Operações do Caixa', icon: SlidersHorizontal, roles: ['super_admin','admin','manager','cashier'] },
  { to: '/saida', label: 'Saída manual', icon: LogOut, roles: ['super_admin','admin','manager','cashier','waiter'] },
  { to: '/observacoes', label: 'Observações', icon: FileText, roles: ['super_admin','admin','manager','cashier','waiter','kitchen','bar'] },
  { to: '/fichas', label: 'Fichas de bebidas', icon: Ticket, roles: ['super_admin','admin','manager','cashier','waiter'] },
  { to: '/recepcao', label: 'Recepção / Bebidas', icon: GlassWater, roles: ['super_admin','admin','manager','cashier'] },
  { to: '/relatorios', label: 'Relatórios', icon: BarChart3, roles: ['super_admin','admin','manager','finance'] },
  { to: '/funcionarios', label: 'Funcionários', icon: UsersRound, roles: ['super_admin','admin','manager'] },
  { to: '/cozinha', label: 'Cozinha / KDS', icon: ChefHat, roles: ['super_admin','admin','manager','kitchen','bar'] },
]

const navSections = [
  {label:'Início',paths:['/painel','/garcom']},
  {label:'Atendimento',paths:['/mesas','/pedidos','/comandas','/vendas-rapidas','/clientes','/saida','/observacoes']},
  {label:'Cardápio e produção',paths:['/produtos','/cozinha','/recepcao','/fichas']},
  {label:'Caixa e gestão',paths:['/caixa','/caixa-operacoes','/contas-a-pagar','/fiado','/estoque','/fidelidade','/relatorios']},
  {label:'Administração',paths:['/funcionarios','/cancelamentos','/auditoria-mesas','/fiscal']}
] as const

export function AppShell() {
  const [open, setOpen] = useState(false)
  const [soundEnabled,setSoundEnabledState]=useState(()=>getSoundEnabled('staff'))
  const { user, profile, signOut } = useAuth()
  const location=useLocation()
  const role = profile?.role || ''

  useEffect(()=>{setOpen(false)},[location.pathname])

  useEffect(()=>{
    if(!profile?.active||!user?.id)return
    const channel=supabase.channel(`global-sound:${user.id}`)
      .on('postgres_changes',{event:'UPDATE',schema:'public',table:'dining_tables'},(payload:any)=>{
        const status=String(payload.new?.status||'')
        if(status==='service_requested'||status==='bill_requested')playNotificationSound('urgent','staff')
      })
      .on('postgres_changes',{event:'INSERT',schema:'public',table:'kitchen_tickets'},()=>{if(location.pathname!=='/caixa')playNotificationSound('order','staff')})
      .on('postgres_changes',{event:'UPDATE',schema:'public',table:'kitchen_tickets'},(payload:any)=>{
        const next=String(payload.new?.status||'')
        const previous=String(payload.old?.status||'')
        if(next==='ready'&&previous!=='ready')playNotificationSound('urgent','staff')
      })
      .on('postgres_changes',{event:'INSERT',schema:'public',table:'guest_payment_requests'},()=>playNotificationSound('payment','staff'))
      .on('postgres_changes',{event:'INSERT',schema:'public',table:'item_cancellation_requests'},()=>playNotificationSound('urgent','staff'))
      .on('postgres_changes',{event:'INSERT',schema:'public',table:'order_cancellation_requests'},()=>playNotificationSound('urgent','staff'))
      .subscribe()
    return()=>{void supabase.removeChannel(channel)}
  },[user?.id,profile?.active,location.pathname])

  const allowedLinks=useMemo(()=>links.filter(link=>link.roles.includes(role)),[role])
  const mobileLinks=useMemo(()=>{
    const wanted=role==='waiter'
      ? ['/garcom','/mesas','/pedidos','/clientes']
      : ['super_admin','admin','manager'].includes(role)
        ? ['/painel','/mesas','/pedidos','/caixa']
        : role==='cashier'
          ? ['/painel','/clientes','/pedidos','/caixa','/vendas-rapidas']
          : allowedLinks.slice(0,4).map(link=>link.to)
    return wanted.map(path=>links.find(link=>link.to===path)).filter(Boolean) as typeof links
  },[role,allowedLinks])

  function toggleSound(){
    const next=!soundEnabled
    setSoundEnabled(next,'staff')
    setSoundEnabledState(next)
    if(next)window.setTimeout(()=>playNotificationSound('success','staff'),20)
  }

  return <div className="app-shell">
    <aside className={open ? 'sidebar open' : 'sidebar'} aria-label="Navegação principal">
      <div className="sidebar-mobile-head">
        <div className="brand"><span className="brand-mark">G</span><div><strong>GONÇALO</strong><small>Music Bar</small></div></div>
        <button className="sidebar-close" onClick={()=>setOpen(false)} aria-label="Fechar menu"><PanelLeftClose/></button>
      </div>
      <nav aria-label="Módulos do sistema">{navSections.map(section=>{
        const sectionLinks=allowedLinks.filter(link=>(section.paths as readonly string[]).includes(link.to))
        if(!sectionLinks.length)return null
        return <div className="sidebar-section" key={section.label}>
          <span className="sidebar-section-heading">{section.label}</span>
          {sectionLinks.map(({to,label,icon:Icon})=><NavLink key={to} to={to} end={to==='/painel'} onClick={()=>setOpen(false)}><Icon size={19}/><span>{label}</span></NavLink>)}
        </div>
      })}</nav>
      <div className="sidebar-footer">
        {['super_admin','admin','manager'].includes(role) && <NavLink to="/configuracoes"><Settings size={20}/><span>Configurações</span></NavLink>}
        <button onClick={() => void signOut()}><LogOut size={20}/><span>Sair</span></button>
      </div>
    </aside>

    {open && <button className="backdrop" aria-label="Fechar menu" onClick={() => setOpen(false)}/>} 

    <main className="main-area">
      <header className="topbar">
        <button className="icon-button" onClick={() => setOpen(v => !v)} aria-label="Abrir menu"><Menu/></button>
        <div className="topbar-status"><span className="online-dot"/> Operação online</div>
        <button type="button" className={`sound-toggle ${soundEnabled?'active':''}`} onClick={toggleSound} title={soundEnabled?'Som das notificações ligado':'Ativar som das notificações'} aria-label={soundEnabled?'Desativar som das notificações':'Ativar som das notificações'}>{soundEnabled?<Volume2 size={19}/>:<VolumeX size={19}/>}<span>{soundEnabled?'Som ligado':'Som desligado'}</span></button>
        <div className="user-chip" title={profile?.full_name || user?.email}>{(profile?.full_name || user?.email)?.slice(0, 2).toUpperCase() || 'GM'}</div>
      </header>
      <section className="content"><Outlet/></section>
    </main>

    <nav className="mobile-bottom-nav" aria-label="Atalhos principais">
      {mobileLinks.map(({to,label,icon:Icon})=><NavLink key={to} to={to} end={to==='/painel'}><Icon size={21}/><span>{label.replace('Visão geral','Início').replace('Balcão / Delivery','Balcão')}</span></NavLink>)}
      <button type="button" onClick={()=>setOpen(true)}><Menu size={21}/><span>Mais</span></button>
    </nav>
  </div>
}
